import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "../../../../context/CurrencyContext";
import { getPackagesFromDB } from "../../../../services/pricingService";
import "../../../Doctor/Packages.css";

const HomePackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedCurrency, formatPrice, convertPrice, isLoading: currencyLoading } = useCurrency();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const packagesData = await getPackagesFromDB();
      setPackages(packagesData);
    } catch (error) {
      console.error('Error fetching packages:', error);
      // Fallback to default packages if DB fails
      setPackages(getDefaultPackages());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPackages = () => [
    {
      name: "Free",
      price: 0,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "no",
      chatsupport: "no",
      featuredprofile: "no",
      profilewebsite: "no",
      brochures: "no",
      articles: "no",
      hms: "no",
      awards: "no",
      memberships: "no",
      socialposts: "no",
      socialvideos: "no",
      duration: 0,
      featureddays: 0,
      key: "free"
    },
    {
      name: "Basic",
      price: 1500,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "no",
      chatsupport: "no",
      featuredprofile: "no",
      profilewebsite: "no",
      brochures: "no",
      articles: "no",
      hms: "no",
      awards: "no",
      memberships: "no",
      socialposts: "no",
      socialvideos: "no",
      duration: 30,
      featureddays: 0,
      key: "basic"
    },
    {
      name: "Business",
      price: 2500,
      period: "Monthly",
      baseCurrency: 'INR',
      autopost: "yes",
      chatsupport: "yes",
      featuredprofile: "yes",
      profilewebsite: "yes",
      brochures: "10",
      articles: "15",
      hms: "yes",
      awards: "10",
      memberships: "10",
      socialposts: "30",
      socialvideos: "10",
      duration: 30,
      featureddays: 100,
      key: "business"
    }
  ];

  const getDisplayPrice = async (pkg) => {
    if (pkg.baseCurrency === selectedCurrency) {
      return { amount: pkg.price, symbol: getCurrencySymbol(selectedCurrency) };
    }

    try {
      const convertedAmount = await convertPrice(pkg.price, pkg.baseCurrency, selectedCurrency);
      return { amount: convertedAmount, symbol: getCurrencySymbol(selectedCurrency) };
    } catch (error) {
      console.error('Price conversion failed:', error);
      return { amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) };
    }
  };

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 
      'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
    };
    return currencyMap[currency] || '₹';
  };

  if (loading) {
    return (
      <section className="our-services">
        <div className="container">
          <div className="packages-header" style={{ marginBottom: "2rem", textAlign: "center" }}>
            <h2 className="section-title">Loading Packages...</h2>
            <div className="loading-spinner">⏳</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="our-services" style={{ padding: '4rem 0', margin: '0' }}>
      <div className="container">
        <div className="packages-header" style={{ marginBottom: "2rem", textAlign: "center" }}>
          <h2 className="section-title" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Affordable Packages</h2>
          <p className="section-description" style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            Pick a plan that fits your practice. Upgrade anytime.
            <span className="currency-note" style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>
              Prices shown in {selectedCurrency}
              {currencyLoading && <span style={{ marginLeft: '0.5rem' }}>🔄</span>}
            </span>
          </p>
        </div>

        <div className="packages-grid">
          {packages.map((pkg, idx) => (
            <PackageCard 
              key={pkg.key} 
              pkg={pkg} 
              idx={idx} 
              selectedCurrency={selectedCurrency}
              getDisplayPrice={getDisplayPrice}
              getCurrencySymbol={getCurrencySymbol}
            />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Link to="/packages" className="dc-btn dc-btnactive">
            View All Packages
            <i className="fas fa-arrow-right" style={{ marginLeft: '8px' }}></i>
          </Link>
        </div>
      </div>
    </section>
  );
};


// Separate component for package card with dynamic pricing
const PackageCard = ({ pkg, idx, selectedCurrency, getDisplayPrice, getCurrencySymbol }) => {
  const [displayPrice, setDisplayPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    loadPrice();
  }, [pkg, selectedCurrency]);

  const loadPrice = async () => {
    setPriceLoading(true);
    try {
      const price = await getDisplayPrice(pkg);
      setDisplayPrice(price);
    } catch (error) {
      console.error('Error loading price:', error);
      setDisplayPrice({ amount: pkg.price, symbol: getCurrencySymbol(pkg.baseCurrency) });
    } finally {
      setPriceLoading(false);
    }
  };

  if (!displayPrice) {
    return (
      <div className="package-card">
        <div className="package-header">
          <h3>{pkg.name}</h3>
          <div className="package-price">
            <span className="loading-price">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`package-card ${idx === 1 ? 'is-featured' : ''}`}>
      {idx === 1 && <div className="package-badge">POPULAR</div>}
      <div className="package-header">
        <h3>{pkg.name}</h3>
        <div className="package-price">
          <span className="currency">{displayPrice.symbol}</span>
          <span className="amount">
            {priceLoading ? '...' : displayPrice.amount.toFixed(2)}
          </span>
          <span className="period">/{pkg.period}</span>
        </div>
        {selectedCurrency !== pkg.baseCurrency && (
          <div className="original-price" style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
            Original: {selectedCurrency} {displayPrice.amount.toFixed(2)}
          </div>
        )}
      </div>

      <div className="package-features">
        <ul>
          {pkg.autopost && <li><span className="feature-label">Auto Post</span> <span className="feature-value">{pkg.autopost == "no" ? '✗' : '✓'}</span> </li>}
          {pkg.chatsupport && <li><span className="feature-label">Chat Support</span> <span className="feature-value">{pkg.chatsupport == "no" ? '✗' : '✓'}</span></li>}
          {pkg.featuredprofile && <li><span className="feature-label">Featured Profile</span> <span className="feature-value">{pkg.featuredprofile == "no" ? '✗' : '✓'}</span></li>}
          {pkg.profilewebsite && <li><span className="feature-label">Profile Website</span> <span className="feature-value">{pkg.profilewebsite == "no" ? '✗' : '✓'}</span></li>}
          {pkg.brochures && <li><span className="feature-label">Brochures</span> <span className="feature-value">{pkg.brochures == "no" ? '✗' : pkg.brochures}</span></li>}
          {pkg.articles && <li><span className="feature-label">Articles</span> <span className="feature-value">{pkg.articles == "no" ? '✗' : pkg.articles}</span></li>}
          <li><span className="feature-label">Listing</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Digital Visiting Card</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Bookings</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">Quick Chat</span> <span className="feature-value">✓</span></li>
          <li><span className="feature-label">HMS Integration</span> <span className="feature-value">{pkg.hms == "no" ? '✗' : '✓'}</span></li>
          {pkg.memberships && <li><span className="feature-label">Memberships</span> <span className="feature-value">{pkg.memberships == "no" ? '✗' : pkg.memberships}</span></li>}
          {pkg.socialposts && <li><span className="feature-label">Social Posts</span> <span className="feature-value">{pkg.socialposts}</span></li>}
          {pkg.socialvideos && <li><span className="feature-label">Social Videos</span> <span className="feature-value">{pkg.socialvideos}</span></li>}
          <li><span className="feature-label">Featured Days</span> <span className="feature-value">{pkg.featureddays}</span></li>
        </ul>
      </div>

      <Link to={`/package/${pkg.key}`} className="dc-btn dc-btnactive" data-key={pkg.key}>
        {pkg.name === "Free" ? "Get Started" : "Choose Plan"}
      </Link>
    </div>
  );
};

export default HomePackages;
